// =============================================================================
// src/features/reputation/index.ts
//
// src/features/reputation/index.ts — markup do takeover Gestao de Reputacao (unified.html 2358-2752). Runtime: assets/reputation.js.
// =============================================================================

import { resolveOwnerIdentity } from '../../app/owner-identity';
import { getLiveReputationIndex } from './reputation-live-store';
import { formatReputationNumber, reputationBand } from './reputation-data';

/** Markup do takeover. Thunk (não const) para ler resolveOwnerIdentity() + o índice
 *  vivo (getLiveReputationIndex) em call-time, após a hidratação no bootstrap. */
const renderMarkup = (): string => `<div id="reputationTakeover" class="reputation-takeover" role="dialog" aria-modal="true" aria-labelledby="repTitle" hidden>

  <header class="rep-head">
    <div class="rep-brand">
      <span class="rep-mark" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M4 16a8 8 0 0 1 16 0" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
          <line x1="12" y1="16" x2="15.4" y2="11.6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
          <circle cx="12" cy="16" r="1.6" fill="currentColor"/>
        </svg>
      </span>
      <div>
        <div class="rep-brand-title" id="repTitle">Gestão de Reputação</div>
        <div class="rep-brand-sub">Mapa Político · Manaus</div>
      </div>
    </div>

    <div class="rep-ctx">
      <span class="rep-ctx-dot" id="repCtxDot" title="Bom"></span>
      <div style="min-width:0">
        <div class="rep-ctx-name">${resolveOwnerIdentity().name}</div>
        <div class="rep-ctx-meta">Reputation Index ${formatReputationNumber(getLiveReputationIndex())} · ${reputationBand(getLiveReputationIndex()).name}</div>
      </div>
    </div>

    <div class="rep-head-spacer"></div>

    <div class="rep-seg" role="group" aria-label="Seções da reputação">
      <button type="button" class="rep-seg-btn active" data-rep-view="overview">Visão geral</button>
      <button type="button" class="rep-seg-btn" data-rep-view="compare">Comparador</button>
      <button type="button" class="rep-seg-btn" data-rep-view="sources">Fontes &amp; ações</button>
    </div>

    <button type="button" class="rep-close" id="repClose" aria-label="Voltar ao mapa">
      <i class="ti ti-x rep-ico sm" aria-hidden="true"></i> Voltar ao mapa
    </button>
  </header>

  <!-- Ticker ao vivo (estilo financeiro) -->
  <div class="rep-ticker" id="repTicker" aria-label="Feed ao vivo de menções e métricas de reputação">
    <div class="rep-ticker-live">
      <span class="rep-ticker-pip" aria-hidden="true"></span> Ao vivo
    </div>
    <div class="rep-ticker-viewport">
      <div class="rep-ticker-track" id="repTickerTrack"></div>
    </div>
    <button type="button" class="rep-ticker-toggle" id="repTickerToggle"
            aria-label="Pausar feed" aria-pressed="false">
      <i class="ti ti-player-pause-filled rep-ico sm" aria-hidden="true"></i>
    </button>
  </div>

  <div class="rep-scroll">
    <main class="rep-main">

      <!-- ============ VIEW: VISÃO GERAL ============ -->
      <div id="repViewOverview" class="rep-flow">

        <!-- Banner de KPIs (prova de valor) -->
        <section class="rep-kpis" aria-label="Indicadores da plataforma">
          <div class="rep-kpi">
            <span class="rep-kpi-val">+12</span>
            <span class="rep-kpi-lbl">bases monitoradas</span>
            <span class="rep-kpi-foot">em tempo real</span>
          </div>
          <div class="rep-kpi">
            <span class="rep-kpi-val">90%</span>
            <span class="rep-kpi-lbl">menos tempo de resposta</span>
            <span class="rep-kpi-foot">da detecção à ação</span>
          </div>
          <div class="rep-kpi">
            <span class="rep-kpi-val">24/7</span>
            <span class="rep-kpi-lbl">análise contínua</span>
            <span class="rep-kpi-foot">histórico + relatórios</span>
          </div>
          <div class="rep-kpi">
            <span class="rep-kpi-val">+70%</span>
            <span class="rep-kpi-lbl">engajamento com públicos</span>
            <span class="rep-kpi-foot">eleitorado e aliados</span>
          </div>
        </section>

        <!-- Reputation Index: gauge + régua + dimensões -->
        <section class="rep-index">
          <div class="rep-index-head">
            <div>
              <div class="rep-eyebrow">Reputation Index <span class="sep"></span> atualizado há 6 min</div>
              <p class="rep-index-lede">Não é só monitorar menções: o índice <strong>organiza e interpreta</strong> a percepção pública e mostra o impacto real na imagem do mandato, num único número de 0 a 100.</p>
            </div>
          </div>

          <div class="rep-index-body">
            <div class="rep-gauge" role="img" aria-label="Reputation Index 53,15 de 100, classificação Bom, tendência de leve queda">
              <svg viewBox="0 0 220 124" aria-hidden="true">
                <path class="rep-band crit" d="M22 104 A88 88 0 0 1 47.8 41.8"  fill="none" stroke-width="13"/>
                <path class="rep-band reg"  d="M47.8 41.8 A88 88 0 0 1 110 16"   fill="none" stroke-width="13"/>
                <path class="rep-band good" d="M110 16 A88 88 0 0 1 172.2 41.8"  fill="none" stroke-width="13"/>
                <path class="rep-band exc"  d="M172.2 41.8 A88 88 0 0 1 198 104"  fill="none" stroke-width="13"/>
                <line id="repGaugeNeedle" class="rep-needle" x1="110" y1="104" x2="110" y2="34" stroke-width="3" stroke-linecap="round"/>
                <circle class="rep-needle-pivot" cx="110" cy="104" r="6"/>
              </svg>
              <div class="rep-gauge-read">
                <span class="rep-gauge-val" id="repGaugeVal">53,15</span>
                <span class="rep-gauge-class" id="repGaugeClass">Bom</span>
              </div>
              <div class="rep-gauge-rule">Bom: entre 50 e 75</div>
            </div>

            <div class="rep-dims">
              <div class="rep-dims-rule" aria-hidden="true">
                <span class="seg crit">Crítico</span>
                <span class="seg reg">Regular</span>
                <span class="seg good">Bom</span>
                <span class="seg exc">Excelente</span>
              </div>
              <p class="rep-dims-intro">O índice se decompõe em três dimensões que explicam o porquê do número:</p>

              <div class="rep-dim">
                <div class="rep-dim-top">
                  <span class="rep-dim-name">Percepção <span class="rep-dim-tag">NRS</span></span>
                  <span class="rep-dim-val" data-band="58">58</span>
                </div>
                <div class="rep-dim-track"><span class="rep-dim-fill" style="width:58%"></span></div>
                <span class="rep-dim-desc">Qualidade e equilíbrio do sentimento nas menções.</span>
              </div>
              <div class="rep-dim">
                <div class="rep-dim-top">
                  <span class="rep-dim-name">Presença <span class="rep-dim-tag">SOV</span></span>
                  <span class="rep-dim-val" data-band="49">49</span>
                </div>
                <div class="rep-dim-track"><span class="rep-dim-fill" style="width:49%"></span></div>
                <span class="rep-dim-desc">Dominância do mandato nas conversas do segmento.</span>
              </div>
              <div class="rep-dim">
                <div class="rep-dim-top">
                  <span class="rep-dim-name">Protagonismo <span class="rep-dim-tag">BPS</span></span>
                  <span class="rep-dim-val" data-band="61">61</span>
                </div>
                <div class="rep-dim-track"><span class="rep-dim-fill" style="width:61%"></span></div>
                <span class="rep-dim-desc">Centralidade do mandato como assunto principal.</span>
              </div>
            </div>
          </div>
        </section>

        <!-- Série histórica + Share of Voice -->
        <div class="rep-grid-2">
          <section class="rep-panel">
            <div class="rep-panel-head">
              <div class="rep-panel-eyebrow"><i class="ti ti-chart-line rep-ico sm" aria-hidden="true"></i> Evolução do índice</div>
              <h3 class="rep-panel-title">Reputation Index ao longo do tempo</h3>
            </div>
            <div class="rep-panel-body">
              <div class="rep-evo-plot" id="repTrendPlot" tabindex="0" role="slider"
                   aria-label="Evolução do Reputation Index"
                   aria-valuemin="0" aria-valuemax="9" aria-valuenow="9"
                   aria-valuetext="53,15, Bom, agora">
                <svg class="rep-evo" viewBox="0 0 600 150" preserveAspectRatio="none" aria-hidden="true">
                  <defs>
                    <linearGradient id="repTrendFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0" stop-color="rgba(24,95,165,0.18)"/>
                      <stop offset="1" stop-color="rgba(24,95,165,0)"/>
                    </linearGradient>
                  </defs>
                  <line class="rep-evo-grid" x1="0" y1="38"  x2="600" y2="38"/>
                  <line class="rep-evo-grid" x1="0" y1="75"  x2="600" y2="75"/>
                  <line class="rep-evo-grid" x1="0" y1="112" x2="600" y2="112"/>
                  <path class="rep-evo-area" id="repTrendArea" d=""/>
                  <path class="rep-evo-line" id="repTrendLine" d="" vector-effect="non-scaling-stroke"/>
                  <line class="rep-evo-marker" id="repTrendMark" x1="0" y1="0" x2="0" y2="150"/>
                </svg>
                <div class="rep-evo-yaxis" aria-hidden="true"><span>100</span><span>80</span><span>60</span><span>40</span></div>
                <div class="rep-evo-guide" aria-hidden="true"></div>
                <div class="rep-evo-dot" aria-hidden="true"></div>
                <div class="rep-evo-tip" aria-hidden="true">
                  <span class="rep-evo-tip-val">53,15</span><span class="rep-evo-tip-time">agora</span>
                </div>
                <div class="rep-evo-anchor" id="repTrendAnchor" aria-hidden="true">
                  <span class="a-val">53,15</span><span class="a-class">Bom</span>
                </div>
              </div>
              <div class="rep-axis-x"><span>27 jul</span><span>ago</span><span>set</span><span>out</span><span>nov</span><span>agora</span></div>
              <p class="rep-sr-only" id="repTrendLive" aria-live="polite"></p>
            </div>
          </section>

          <section class="rep-panel">
            <div class="rep-panel-head">
              <div class="rep-panel-eyebrow"><i class="ti ti-chart-donut rep-ico sm" aria-hidden="true"></i> Share of Voice</div>
              <h3 class="rep-panel-title">Quem domina a conversa</h3>
            </div>
            <div class="rep-panel-body rep-sov-body">
              <div class="rep-donut">
                <svg viewBox="0 0 180 180" id="repDonut" aria-hidden="true"></svg>
                <div class="rep-donut-center">
                  <span class="rep-donut-total">15 mil</span>
                  <span class="rep-donut-sub">publicações</span>
                </div>
              </div>
              <ul class="rep-sov-legend" id="repSovLegend"></ul>
            </div>
          </section>
        </div>

        <!-- Barras por canal + Menções negativas -->
        <div class="rep-grid-2">
          <section class="rep-panel">
            <div class="rep-panel-head">
              <div class="rep-panel-eyebrow"><i class="ti ti-stack-2 rep-ico sm" aria-hidden="true"></i> Composição por canal</div>
              <h3 class="rep-panel-title">Menções por canal e por nome</h3>
            </div>
            <div class="rep-panel-body" id="repChannels"></div>
          </section>

          <section class="rep-panel">
            <div class="rep-panel-head">
              <div class="rep-panel-eyebrow"><i class="ti ti-activity rep-ico sm" aria-hidden="true"></i> Sinal de risco</div>
              <h3 class="rep-panel-title">Menções negativas por dia</h3>
            </div>
            <div class="rep-panel-body">
              <svg class="rep-neg" viewBox="0 0 600 150" preserveAspectRatio="none" aria-hidden="true" id="repNegChart">
                <line class="rep-evo-grid" x1="0" y1="37"  x2="600" y2="37"/>
                <line class="rep-evo-grid" x1="0" y1="75"  x2="600" y2="75"/>
                <line class="rep-evo-grid" x1="0" y1="113" x2="600" y2="113"/>
                <polyline class="rep-neg-line s1" id="repNeg1" fill="none" points=""/>
                <polyline class="rep-neg-line s2" id="repNeg2" fill="none" points=""/>
                <polyline class="rep-neg-line s3" id="repNeg3" fill="none" points=""/>
              </svg>
              <div class="rep-axis-x"><span>14/09</span><span>15/09</span><span>16/09</span><span>17/09</span></div>
              <ul class="rep-neg-legend" id="repNegLegend"></ul>
            </div>
          </section>
        </div>

        <!-- Alerta WhatsApp + ponte para ação -->
        <div class="rep-grid-2 rep-grid-alert">
          <section class="rep-wa" aria-label="Alerta em tempo real">
            <div class="rep-wa-head">
              <span class="rep-wa-mark" aria-hidden="true"><i class="ti ti-brand-whatsapp"></i></span>
              <div class="rep-wa-id">
                <div class="rep-wa-app">Alertas Knewin</div>
                <div class="rep-wa-sub"><span class="rep-wa-online">online</span> · canal Reputação</div>
              </div>
              <span class="rep-wa-sev">Crítico</span>
            </div>
            <div class="rep-wa-thread">
              <div class="rep-wa-bubble">
                <p class="rep-wa-alert"><i class="ti ti-alert-triangle rep-ico sm" aria-hidden="true"></i> Notícia crítica detectada</p>
                <p class="rep-wa-quote">“Contrato da reforma do pronto-socorro da Zona Leste vira alvo de pedido de informação no Senado.”</p>
                <div class="rep-wa-meta">Portal Amazonas Agora · 29/05 · 08h40</div>
                <a class="rep-wa-link" href="#" onclick="return false;">Abrir matéria <i class="ti ti-external-link rep-ico sm" aria-hidden="true"></i></a>
                <p class="rep-wa-impact"><i class="ti ti-trending-down rep-ico sm" aria-hidden="true"></i> Impacto direto na reputação · índice <b>−2,4</b> em 24h</p>
                <span class="rep-wa-time">08:41</span>
              </div>
            </div>
          </section>

          <section class="rep-bridge">
            <div class="rep-bridge-eyebrow"><i class="ti ti-arrow-right rep-ico sm" aria-hidden="true"></i> Da menção à ação</div>
            <p class="rep-bridge-lede">O monitoramento mostra <em>o que</em> está sendo dito. A gestão de reputação responde <em>o que fazer</em> — priorizando riscos e oportunidades por público.</p>
            <button type="button" class="rep-btn rep-btn-primary" data-rep-go="sources">
              Ver ações por público <i class="ti ti-chevron-right rep-ico sm" aria-hidden="true"></i>
            </button>
            <button type="button" class="rep-btn rep-btn-ghost" data-rep-go="compare">
              Comparar com concorrentes
            </button>
          </section>
        </div>

      </div>

      <!-- ============ VIEW: COMPARADOR ============ -->
      <div id="repViewCompare" class="rep-flow" hidden>

        <section class="rep-panel">
          <div class="rep-panel-head">
            <div class="rep-panel-eyebrow"><i class="ti ti-trophy rep-ico sm" aria-hidden="true"></i> Benchmark competitivo</div>
            <h3 class="rep-panel-title">Ranking de reputação no segmento</h3>
            <p class="rep-panel-sub">Reputation Index dos principais nomes de Manaus, classificado por faixa. Linha tracejada = média do segmento.</p>
          </div>
          <div class="rep-panel-body">
            <div class="rep-rank" id="repRank"></div>
            <div class="rep-rank-legend">
              <span><i class="dot crit"></i> Crítico (0–25)</span>
              <span><i class="dot reg"></i> Regular (25–50)</span>
              <span><i class="dot good"></i> Bom (50–75)</span>
              <span><i class="dot exc"></i> Excelente (75–100)</span>
            </div>
          </div>
        </section>

        <section class="rep-panel rep-duel">
          <div class="rep-panel-head">
            <div class="rep-panel-eyebrow"><i class="ti ti-versus rep-ico sm" aria-hidden="true"></i> Comparação direta</div>
            <h3 class="rep-panel-title">Duelo entre dois nomes</h3>
          </div>
          <div class="rep-panel-body">
            <div class="rep-duel-pick">
              <label class="rep-duel-side a">
                <span class="rep-duel-side-lbl">Lado A</span>
                <select class="rep-select" id="repDuelA" aria-label="Político lado A"></select>
              </label>
              <span class="rep-duel-vs" aria-hidden="true">vs</span>
              <label class="rep-duel-side b">
                <span class="rep-duel-side-lbl">Lado B</span>
                <select class="rep-select" id="repDuelB" aria-label="Político lado B"></select>
              </label>
            </div>
            <div class="rep-duel-grid" id="repDuelGrid"></div>
            <p class="rep-duel-verdict" id="repDuelVerdict"></p>
          </div>
        </section>

      </div>

      <!-- ============ VIEW: FONTES & AÇÕES ============ -->
      <div id="repViewSources" class="rep-flow" hidden>

        <div class="rep-grid-2 rep-grid-sources">
          <section class="rep-panel">
            <div class="rep-panel-head">
              <div class="rep-panel-eyebrow"><i class="ti ti-database rep-ico sm" aria-hidden="true"></i> Coleta</div>
              <h3 class="rep-panel-title">Fontes monitoradas em tempo real</h3>
              <p class="rep-panel-sub">Mais de 12 bases varridas continuamente — a matéria-prima do índice.</p>
            </div>
            <div class="rep-panel-body">
              <ul class="rep-src-list">
                <li class="rep-src"><span class="rep-src-ic"><i class="ti ti-world" aria-hidden="true"></i></span><span class="rep-src-name">Mídias online</span><span class="rep-src-meta">portais e blogs · 9.7 mil/dia</span></li>
                <li class="rep-src"><span class="rep-src-ic"><i class="ti ti-broadcast" aria-hidden="true"></i></span><span class="rep-src-name">Mídias offline</span><span class="rep-src-meta">TV, rádio e impresso · 640/dia</span></li>
                <li class="rep-src"><span class="rep-src-ic"><i class="ti ti-brand-x" aria-hidden="true"></i></span><span class="rep-src-name">Redes sociais</span><span class="rep-src-meta">perfis e grupos · 3.1 mil/dia</span></li>
                <li class="rep-src"><span class="rep-src-ic"><i class="ti ti-microphone-2" aria-hidden="true"></i></span><span class="rep-src-name">Podcasts</span><span class="rep-src-meta">transcrição por áudio · 120/dia</span></li>
                <li class="rep-src"><span class="rep-src-ic"><i class="ti ti-file-text" aria-hidden="true"></i></span><span class="rep-src-name">Diário Oficial e relatórios</span><span class="rep-src-meta">atos e contratos · 80/dia</span></li>
                <li class="rep-src"><span class="rep-src-ic"><i class="ti ti-message-report" aria-hidden="true"></i></span><span class="rep-src-name">Sites de reclamação</span><span class="rep-src-meta">ouvidoria e fóruns · 210/dia</span></li>
              </ul>
            </div>
          </section>

          <section class="rep-panel">
            <div class="rep-panel-head">
              <div class="rep-panel-eyebrow"><i class="ti ti-route rep-ico sm" aria-hidden="true"></i> Cadeia de valor</div>
              <h3 class="rep-panel-title">Como o número vira ação</h3>
            </div>
            <div class="rep-panel-body">
              <ol class="rep-chain">
                <li><span class="rep-chain-n">1</span><div><b>Coleta</b><span>12+ bases, tempo real</span></div></li>
                <li><span class="rep-chain-n">2</span><div><b>Processamento</b><span>sentimento, SOV, protagonismo</span></div></li>
                <li><span class="rep-chain-n">3</span><div><b>Síntese</b><span>Reputation Index 0–100</span></div></li>
                <li><span class="rep-chain-n">4</span><div><b>Visualização</b><span>gauge, série, ranking</span></div></li>
                <li><span class="rep-chain-n">5</span><div><b>Ação</b><span>alertas + recomendações por público</span></div></li>
              </ol>
            </div>
          </section>
        </div>

        <section class="rep-panel">
          <div class="rep-panel-head">
            <div class="rep-panel-eyebrow"><i class="ti ti-users-group rep-ico sm" aria-hidden="true"></i> Prioridades por público</div>
            <h3 class="rep-panel-title">O que fazer, segmentado por stakeholder</h3>
            <p class="rep-panel-sub">A etapa que separa gestão de reputação de monitoramento puro.</p>
          </div>
          <div class="rep-panel-body">
            <div class="rep-stk-grid">
              <article class="rep-stk">
                <div class="rep-stk-head"><i class="ti ti-vote rep-ico" aria-hidden="true"></i> Eleitorado da Zona Leste</div>
                <span class="rep-tag risk">Risco</span>
                <p class="rep-stk-action">Publicar nota factual com o edital e o cronograma da obra antes das 14h, no canal de maior alcance local.</p>
                <span class="rep-stk-foot">priorizado pelo pico de menções negativas</span>
              </article>
              <article class="rep-stk">
                <div class="rep-stk-head"><i class="ti ti-shield-check rep-ico" aria-hidden="true"></i> Base &amp; aliados</div>
                <span class="rep-tag opp">Oportunidade</span>
                <p class="rep-stk-action">Municiar lideranças com 3 dados de transparência para amplificar a resposta de forma coordenada.</p>
                <span class="rep-stk-foot">apoio orgânico cresceu 11% hoje</span>
              </article>
              <article class="rep-stk">
                <div class="rep-stk-head"><i class="ti ti-news rep-ico" aria-hidden="true"></i> Imprensa</div>
                <span class="rep-tag risk">Risco</span>
                <p class="rep-stk-action">Oferecer entrevista exclusiva a veículo de referência para fixar a versão correta antes do ciclo da tarde.</p>
                <span class="rep-stk-foot">2 veículos já citaram o edital</span>
              </article>
              <article class="rep-stk">
                <div class="rep-stk-head"><i class="ti ti-businessplan rep-ico" aria-hidden="true"></i> Apoiadores institucionais</div>
                <span class="rep-tag watch">Atenção</span>
                <p class="rep-stk-action">Enviar resumo proativo do andamento e dos próximos passos para evitar leitura de risco.</p>
                <span class="rep-stk-foot">sem dano reputacional até o momento</span>
              </article>
            </div>
          </div>
        </section>

      </div>

    </main>
  </div>
</div>`;

/** Devolve a string HTML literal deste bloco. */
export function buildReputationTakeoverMarkup(): string {
  return renderMarkup();
}

// Comportamento portado para TS (substitui o auto-boot de assets/reputation.js).
// main.ts importa `initReputationTakeover` daqui e chama no bootstrap, no lugar de
// injetar legacy/reputation.js. Re-export agregado mantem o barrel como porta unica.
export { initReputationTakeover } from './reputation-takeover';
